import { GridColumn as Column } from '@progress/kendo-react-grid';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  DeleteButton,
  SecondaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { DropdownCell } from 'src/components/shared/DataGrid/EditableGrid/DropdownCell';
import { EditableGrid } from 'src/components/shared/DataGrid/EditableGrid/EditableGrid';
import { selectMethods } from 'src/store/methods/methods.selectors';
import { selectActiveParameters } from 'src/store/parameters/parameters.selectors';
import {
  SpecificationDetailDto,
  SpecificationParameterDto
} from 'src/store/specifications/specification';
import { isUserAdmin, useAuth } from '../../Auth/AuthContext';

interface Props {
  specification: Nullable<SpecificationDetailDto>;
  onParametersChanged: (parameters: SpecificationParameterDto[]) => void;
}

let uniqueId = 0;

export const Parameters: React.FC<Props> = ({
  specification,
  onParametersChanged
}) => {
  const { t } = useTranslation();

  const [parameters, setParameters] = useState<SpecificationParameterDto[]>([]);

  const { user } = useAuth();
  const parameters$ = useSelector(selectActiveParameters);
  const methods$ = useSelector(selectMethods);

  useEffect(() => {
    if (specification) {
      setParameters(specification.specificationParameters);
    }
  }, [specification]);

  useEffect(() => {
    onParametersChanged(parameters);
  }, [parameters, onParametersChanged]);

  const onCancel = () => {
    setParameters([...(specification?.specificationParameters ?? [])]);
  };

  const onAddParameter = () => {
    const parameter: SpecificationParameterDto = {
      id: --uniqueId,
      specificationId: specification?.id,
      decimalCustomer: null,
      decimalProd: null,
      maxCustomer: null,
      maxProd: null,
      minCustomer: null,
      minProd: null,
      userId: user?.id,
      user: user
    };
    setParameters([parameter, ...parameters]);
  };

  const onDeleteParameter = (parameter: SpecificationParameterDto) => {
    setParameters(parameters.filter(p => p.id !== parameter.id));
  };

  const ParametersList = useMemo(() => {
    return (
      <EditableGrid
        items={parameters}
        onItemsChange={items => {
          setParameters(items);
        }}
      >
        <Column
          field="parameterId"
          title={t('parameter.singular')}
          width="220px"
          cell={props => (
            <DropdownCell
              {...props}
              items={parameters$}
              disabled={!isUserAdmin(user)}
              uniqueField="id"
              optionField="name"
            />
          )}
        />
        <Column
          field="methodId"
          title={t('method.singular')}
          width="220px"
          cell={props => (
            <DropdownCell
              {...props}
              items={methods$.filter(m =>
                m.parameterIds.includes(props.dataItem.parameterId)
              )}
              disabled={!isUserAdmin(user)}
              uniqueField="id"
              optionField="name"
            />
          )}
        />
        <Column
          field="minProd"
          title={t('props.minProd')}
          width="140px"
          editor="numeric"
        />
        <Column
          field="maxProd"
          title={t('props.maxProd')}
          width="140px"
          editor="numeric"
        />
        <Column
          field="decimalProd"
          title={t('props.decimalProd')}
          width="140px"
          editor="numeric"
        />
        <Column
          field="minCustomer"
          title={t('props.minCustomer')}
          width="140px"
          editor="numeric"
        />
        <Column
          field="maxCustomer"
          title={t('props.maxCustomer')}
          width="140px"
          editor="numeric"
        />
        <Column
          field="decimalCustomer"
          title={t('props.decimalCustomer')}
          width="140px"
          editor="numeric"
        />
        <Column
          field="user.initials"
          title={t('user.singular')}
          editable={false}
        />
        <Column
          width="60px"
          filterable={false}
          editable={false}
          cell={props => (
            <td>
              <DeleteButton onClick={() => onDeleteParameter(props.dataItem)} />
            </td>
          )}
        />
      </EditableGrid>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameters, parameters$, methods$, onParametersChanged, t]);

  return (
    <Fragment>
      <div className="flex mb-24">
        <SecondaryButton
          icon="la-plus"
          disabled={!isUserAdmin(user)}
          onClick={onAddParameter}
          className="mr-24"
        >
          {t('add.verb')}
        </SecondaryButton>
        <TertiaryButton disabled={!isUserAdmin(user)} onClick={onCancel}>
          {t('undo')}
        </TertiaryButton>
      </div>
      {ParametersList}
    </Fragment>
  );
};
